function _slicedToArray(r, e) { return _arrayWithHoles(r) || _iterableToArrayLimit(r, e) || _unsupportedIterableToArray(r, e) || _nonIterableRest(); }
function _nonIterableRest() { throw new TypeError("Invalid attempt to destructure non-iterable instance.\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method."); }
function _unsupportedIterableToArray(r, a) { if (r) { if ("string" == typeof r) return _arrayLikeToArray(r, a); var t = {}.toString.call(r).slice(8, -1); return "Object" === t && r.constructor && (t = r.constructor.name), "Map" === t || "Set" === t ? Array.from(r) : "Arguments" === t || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(t) ? _arrayLikeToArray(r, a) : void 0; } }
function _arrayLikeToArray(r, a) { (null == a || a > r.length) && (a = r.length); for (var e = 0, n = Array(a); e < a; e++) n[e] = r[e]; return n; }
function _iterableToArrayLimit(r, l) { var t = null == r ? null : "undefined" != typeof Symbol && r[Symbol.iterator] || r["@@iterator"]; if (null != t) { var e, n, i, u, a = [], f = !0, o = !1; try { if (i = (t = t.call(r)).next, 0 === l) { if (Object(t) !== t) return; f = !1; } else for (; !(f = (e = i.call(t)).done) && (a.push(e.value), a.length !== l); f = !0); } catch (r) { o = !0, n = r; } finally { try { if (!f && null != t.return && (u = t.return(), Object(u) !== u)) return; } finally { if (o) throw n; } } return a; } }
function _arrayWithHoles(r) { if (Array.isArray(r)) return r; }
/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable react/display-name */

import React, { useEffect, useState, useRef, useMemo } from 'react';
import debounce from 'lodash.debounce';
import { assertNotNull } from '@deephaven/utils';
import "./RandomAreaPlotAnimation.css";
import { resolveCssVariablesInRecord, useTheme } from "./theme/index.js";
import { jsx as _jsx } from "react/jsx-runtime";
var VOLATILITY = 0.025; // how spikey the data gets
var LOW = 0.9;
var HIGH = 0.1;
var START_Y = 0.618;
var GRID_SIZE = 80;
var PATTERN = {
  SIZE: 8,
  DOT_SIZE: 2
};
var PX_PER_SECOND = 18;
var RESIZE_DEBOUNCE = 250;
var INTERACTION_TIMEOUT = 60 * 1000;
var THEME_COLOR_VARIABLES = {
  background: 'var(--dh-color-random-area-plot-animation-bg)',
  foregroundFill: 'var(--dh-color-random-area-plot-animation-fg-fill)',
  foregroundStroke: 'var(--dh-color-random-area-plot-animation-fg-stroke)',
  gridColor: 'var(--dh-color-random-area-plot-animation-grid)'
};

/**
 * Resolve theme colors needed for our animation.
 */
function getRandomAreaPlotAnimationThemeColors() {
  return resolveCssVariablesInRecord(THEME_COLOR_VARIABLES);
}

// Draw a background canvas, paint it with a fun chart looking animation
var RandomAreaPlotAnimation = /*#__PURE__*/React.memo(() => {
  var _useTheme = useTheme(),
    activeThemes = _useTheme.activeThemes;
  var themeColors = useMemo(getRandomAreaPlotAnimationThemeColors, [activeThemes]);
  var canvas = useRef(null);
  var container = useRef(null);
  var _useState = useState(false),
    _useState2 = _slicedToArray(_useState, 2),
    shade = _useState2[0],
    setShade = _useState2[1];
  var scale = window.devicePixelRatio;
  var ctx;
  var pattern;
  var width;
  var height;
  var rAF;
  var lastTimestamp = null;
  var data = [];
  function setCanvasSize() {
    var _ctx;
    // Set canvas size to fill screen
    if (container.current != null) {
      width = container.current.offsetWidth;
      height = container.current.offsetHeight;
    }
    if (canvas.current != null) {
      canvas.current.style.width = "".concat(width, "px");
      canvas.current.style.height = "".concat(height, "px");
      canvas.current.width = width * scale;
      canvas.current.height = height * scale;
    }
    (_ctx = ctx) === null || _ctx === void 0 || _ctx.scale(scale, scale);
  }

  // Returns the background fill create offscreen as pattern
  function createPatternFill() {
    var foregroundFill = themeColors.foregroundFill,
      foregroundStroke = themeColors.foregroundStroke;

    // create the off-screen canvas
    var canvasPattern = document.createElement('canvas');
    canvasPattern.width = PATTERN.SIZE;
    canvasPattern.height = PATTERN.SIZE;
    var contextPattern = canvasPattern.getContext('2d');

    // draw pattern to off-screen context
    if (contextPattern != null) {
      contextPattern.fillStyle = foregroundStroke;
      contextPattern.fillRect(0, 0, PATTERN.DOT_SIZE, PATTERN.DOT_SIZE);

      // add an overlay fill
      contextPattern.fillStyle = foregroundFill;
      contextPattern.fillRect(0, 0, PATTERN.SIZE, PATTERN.SIZE);
    }
    return contextPattern === null || contextPattern === void 0 ? void 0 : contextPattern.createPattern(canvasPattern, 'repeat');
  }

  /**
   * Generates a step in a random walk between LOW - HIGH given a previous value
   * data is always in 0-1 data space so its scaleable
   * data space is converted to screen space inside draw functions
   * @param prev previous random walk step
   * @return new random walk step
   */
  function randomWalk(prev) {
    var rand = Math.random() - 0.5; // give it directionality
    var change = VOLATILITY * rand; // scale by volatity constant

    // ensure directional bounce off approaching edges
    if (prev < HIGH) {
      change = Math.abs(change);
    } else if (prev > LOW) {
      change = -Math.abs(change);
    }
    return prev + change;
  }
  function initData() {
    var startingData = [];
    var y = START_Y; // always start towards the bottom of the screen
    for (var x = 0; x < width + PX_PER_SECOND; x += 1) {
      y = randomWalk(y);
      startingData.push(y);
    }
    return startingData;
  }
  function setDataSize() {
    while (data.length >= width + PX_PER_SECOND) {
      data.pop();
    }
    while (data.length < width + PX_PER_SECOND) {
      data.push(randomWalk(data[data.length - 1]));
    }
  }
  function drawAreaLine(path, context) {
    context.beginPath();
    // start offscreen
    context.moveTo(-1, height + 1);
    context.lineTo(-1, height * path[0]);

    // draw the line
    for (var i = 0; i < path.length; i += 1) {
      context.lineTo(i, height * path[i]);
    }

    // close offscreen
    context.lineTo(width + PX_PER_SECOND, height * path[path.length - 1]);
    context.lineTo(width + PX_PER_SECOND, height + 1);
    context.closePath();
  }
  function drawGridLines(context) {
    context.beginPath();
    // draw verticals
    for (var x = 1; x < width; x += GRID_SIZE) {
      context.moveTo(x, 0);
      context.lineTo(x, height);
    }
    // draw horizontals
    for (var y = 1; y < height; y += GRID_SIZE) {
      context.moveTo(0, y);
      context.lineTo(width, y);
    }
  }

  /**
   * Main draw loop for where the animation happens
   * all the styling happens within drawCanvas from paths generated by other functions
   * @param timestamp passed in callback from requestAnimationFrame
   */
  function drawCanvas(timestamp) {
    lastTimestamp = lastTimestamp !== null && lastTimestamp !== void 0 ? lastTimestamp : timestamp;
    var background = themeColors.background,
      foregroundStroke = themeColors.foregroundStroke,
      gridColor = themeColors.gridColor;
    assertNotNull(ctx);
    // clear the canvas
    ctx.fillStyle = background;
    ctx.fillRect(0, 0, width, height);
    drawGridLines(ctx);
    ctx.lineWidth = 1;
    ctx.strokeStyle = gridColor;
    ctx.stroke();

    // for speeds less then 60 px per second, subpixel motion is required to look smooth
    var subPixelMotion = ((lastTimestamp !== null && lastTimestamp !== void 0 ? lastTimestamp : 0) - (timestamp !== null && timestamp !== void 0 ? timestamp : 0)) / (1000 / PX_PER_SECOND);
    ctx.translate(subPixelMotion, 0);
    drawAreaLine(data, ctx);
    ctx.lineWidth = 2;
    ctx.strokeStyle = foregroundStroke;
    ctx.stroke();
    ctx.translate(-subPixelMotion, 0); // we don't pattern to shift however
    if (pattern != null) {
      ctx.fillStyle = pattern;
    }
    ctx.fill();
    ctx.setTransform(scale, 0, 0, scale, 0, 0); // reset the translate matrix to normal

    // generate new data points
    if ((timestamp !== null && timestamp !== void 0 ? timestamp : 0) - (lastTimestamp !== null && lastTimestamp !== void 0 ? lastTimestamp : 0) > 1000 / PX_PER_SECOND) {
      // number of points to add since last update
      var numberOfPoints = Math.floor(((timestamp !== null && timestamp !== void 0 ? timestamp : 0) - (lastTimestamp !== null && lastTimestamp !== void 0 ? lastTimestamp : 0)) / (1000 / PX_PER_SECOND));
      for (var i = 0; i < numberOfPoints; i += 1) {
        data.shift();
        data.push(randomWalk(data[data.length - 1]));
      }
      lastTimestamp = timestamp;
    }
    if (rAF != null) {
      cancelAnimationFrame(rAF);
    }
    rAF = requestAnimationFrame(drawCanvas);
  }

  // debounce the resize so its not overly aggresive
  var debouncedHandleResize = debounce(() => {
    rAF = requestAnimationFrame(timestamp => {
      setCanvasSize(); // setCanvas can cause lag if not debounced

      // fill or remove extra data points
      setDataSize();

      // opening devtools triggers a blur then resize,
      // need to reset shade as we are restarting animation
      setShade(false);
      drawCanvas(timestamp);
    });
  }, RESIZE_DEBOUNCE, {
    leading: true
  } // resize immediately, once.
  );
  function pauseAnimation() {
    // pause animation when browser window is blurred to conserve CPU usage
    setShade(true);
    if (rAF != null) {
      cancelAnimationFrame(rAF);
    }
    rAF = null;
    lastTimestamp = null;
  }

  // when timeout expires, pause animation
  var resetIdleTimeout = debounce(() => {
    pauseAnimation();
  }, INTERACTION_TIMEOUT);
  function startAnimation() {
    // can fire multiple times, check if rAF is null first
    // restart animation loop, and fade back in
    if (rAF == null) {
      setShade(false);
      setCanvasSize();
      setDataSize();
      rAF = requestAnimationFrame(drawCanvas);
    }
    resetIdleTimeout();
  }
  function handleActivity() {
    if (document.hasFocus()) {
      // only if document has focus (mousemove can fire on non-focused tabs)
      startAnimation();
    }
  }
  function handleResize() {
    // pause and debounce animation during resize for better resizing performance
    if (rAF != null) {
      cancelAnimationFrame(rAF);
    }
    resetIdleTimeout(); // treat resizing as a form of activity
    debouncedHandleResize();
  }

  // react mount/unmount hooks
  useEffect(() => {
    var _canvas$current;
    ctx = (_canvas$current = canvas.current) === null || _canvas$current === void 0 ? void 0 : _canvas$current.getContext('2d', {
      alpha: false
    });
    setCanvasSize();
    data = initData();
    pattern = createPatternFill();

    // start draw loop
    drawCanvas();
    resetIdleTimeout();
    window.addEventListener('resize', handleResize);
    window.addEventListener('focus', startAnimation);
    window.addEventListener('blur', pauseAnimation);

    // don't burn cpu if the user is "idle", start a timer that resets
    // on any user activity, and on timeout expiry pause the animation
    window.addEventListener('mousemove', handleActivity);
    window.addEventListener('keydown', handleActivity);
    return () => {
      if (rAF != null) {
        cancelAnimationFrame(rAF);
      }
      window.removeEventListener('resize', handleResize);
      window.removeEventListener('focus', startAnimation);
      window.removeEventListener('blur', pauseAnimation);
      window.removeEventListener('mousemove', handleActivity);
      window.removeEventListener('keydown', handleActivity);
      resetIdleTimeout.cancel();
      debouncedHandleResize.cancel();
    };
  }, [themeColors]);
  return /*#__PURE__*/_jsx("div", {
    className: "random-area-plot-animation-container",
    ref: container,
    children: /*#__PURE__*/_jsx("canvas", {
      ref: canvas,
      className: shade ? 'shade' : ''
    })
  });
});
export default RandomAreaPlotAnimation;
//# sourceMappingURL=RandomAreaPlotAnimation.js.map